// @ts-strict-ignore
import type { ValidationRejection } from "@altostra/type-validations";

import type { FetchRequestError } from "@lib/errors";
import { FetchInvalidPayloadShapeError } from "@lib/errors";
import type { RemoveFirstArgument } from "@lib/typeHelpers";

import type { AllRouteEndpoints } from "../routeTypes";
import type { InternalGetFetcherFunctionWithShadowError, GetFetcherFunctionWithShadowError } from "../types";
import type { CacheKeyParam, EndpointQueryKey, RootCacheKey } from "./types";

/**
 * Adapter from react-query fetcher function to a more natural fetcher function.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the abort signal as an optional last parameter
 */
export const reactQueryFetcherAdapter = <
  CacheKey extends AllRouteEndpoints,
  QueryKey extends readonly [rootKey: RootCacheKey<CacheKey>, ...originalKey: CacheKeyParam[]],
  Args extends RemoveFirstArgument<QueryKey>,
  Result,
  Error extends FetchRequestError = FetchRequestError,
>(
  cacheKey: CacheKey,
  fetcherFunction: InternalGetFetcherFunctionWithShadowError<Error, CacheKey, QueryKey, Args, Result>,
  validator?: (value: unknown, rejectionReasons?: (rejection: ValidationRejection) => void) => boolean
): GetFetcherFunctionWithShadowError<Error, CacheKey, QueryKey, Args, Result> => {
  return (
    { queryKey: [, ...rest], signal, meta }: EndpointQueryKey<CacheKey, Args>,
    customizedFetcherFunction?: Function,
    directCallFetcher = false,
    args?: Args
  ) => {
    // If directCallFetcher is true, we are calling the fetcher directly with passed in args
    if (directCallFetcher) {
      return fetcherFunction(signal, ...args);
    }

    // If customizedFetcher is provided, we use that instead of the default fetcherFunction
    const activeFetcherFunction = customizedFetcherFunction || fetcherFunction;

    return activeFetcherFunction(signal, ...(rest as Args)).then((result) => {
      FetchInvalidPayloadShapeError.validateShape({
        cacheKey,
        result,
        validator,
        logException: meta?.logException as ((error: Error) => void) | undefined,
      });

      return result;
    });
  };
};
